import React, { useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import styles from './Collections.scss'
import { CollectionCard } from './CollectionCard'
import { collectionsMessages } from './intlMessages'
import * as userActions from 'lib/api/currentUser'
import { showModal } from 'lib/modalUtils'
import { promiseMapAll } from 'lib/utils'
import { useFormatMessage, commonVerbs, Msg, featureNames } from 'lib/localization'
import { collectionActions, collectionSelectors } from 'data/resources/collections'
import { privacyCodeSelectors } from 'data/resources/privacyCodes'
import { userSelectors } from 'data/resources/users'
import { Card } from 'common/styleguide/Card'
import { CTA } from 'common/styleguide/CTA'
import { RadioGroup } from 'common/styleguide/RadioGroup'
import { StandardLayout } from 'common/layouts/StandardLayout'
import popoverMenuStyles from 'common/styleguide/PopoverMenu/PopoverMenu.scss'
import { CREATE_EDIT_COLLECTION_DIALOG } from 'common/modals/CreateEditCollection'
import { SHARE_MODAL } from 'common/modals/ShareModal'
import { withCollapseState } from 'common/layouts/SideNavLayout/SideNavContexts'
import { PopoverFilter } from 'common/PopoverFilter'
import { IeNudgeBar } from 'common/banners/IeNudgeBar'
import { Loadable } from 'common/Loadable'
import { OrderedMap } from 'immutable'
import Helmet from 'react-helmet'
import { FormattedMessage as T, defineMessages } from 'react-intl'
import cx from 'classnames'
import queryString from 'query-string'

export const messages = defineMessages({
  all: {
    id: 'Collections.allCollections',
    defaultMessage: 'All Collections'
  },
  yours: {
    id: 'Collections.yourCollections',
    defaultMessage: 'Yours'
  },
  followed: {
    id: 'Collections.followedCollections',
    defaultMessage: 'Followed'
  },
  loadableText: {
    id: 'Collections.loadableText',
    defaultMessage: 'Loading your collections'
  },
  followToFilter: {
    id: 'Collections.followToFilter',
    defaultMessage: 'Follow a collection to enable filtering'
  }
})

const ownershipFilterOpts = fm => OrderedMap({
  all: fm(messages.all),
  yours: fm(messages.yours),
  followed: fm(messages.followed)
})

const filterCollections = (collections, ownership, cuid) => {
  switch (ownership) {
    case 'yours':
      return collections.filter(c => c.userId === cuid)
    case 'followed':
      return collections.filter(c => c.userId !== cuid)
    default:
      return collections
  }
}

const PersonalCollections = ({
  collections,
  currentUser,
  ...props
}) => {
  const fm = useFormatMessage()
  const options = ownershipFilterOpts(fm)
  const [ownership, setOwnership] = useState('all')
  const filteredCollections = filterCollections(collections, ownership, currentUser.id)
  const disableFilter = !collections.find(c => c.userId !== currentUser.id)
  const shareRoot = `/users/${currentUser.id}`

  return <_Collections
    collections={filteredCollections}
    totalCount={collections.size}
    options={options}
    selected={ownership}
    filterLabel={options.get(ownership)}
    onOwnershipChange={ownership => setOwnership(ownership)}
    currentUser={currentUser}
    disableFilter={disableFilter}
    shareRoot={shareRoot}
    {...props}
  />
}

export const _Collections = ({
  collections,
  totalCount,
  options,
  selected,
  filterLabel,
  onOwnershipChange,
  currentUser,
  disableFilter,
  shareRoot,
  allowVisibilityUpdate,
  readOnly,
  setPreference,
  collapseState,
  collectionsPrivacyCode,
  user,
  renderEmpty,
  emptyComponent
}) => {
  const fm = useFormatMessage()
  const isMobile = collapseState === 'viewport'

  const EmptyComponent = emptyComponent ?? NoCollections

  return (
      <>
        <Helmet title={`${fm(featureNames.collections)} · Ride with GPS `} />
        <IeNudgeBar currentUser={currentUser} setPreference={setPreference} />
        <div className={cx(styles.wrap, isMobile && styles.collapsed)}>
          {totalCount > 0
            ? <>
              <h2 className={styles.collectionsHeader} data-heading-alt>
                <Msg m={featureNames.collections} />
                {readOnly && ` · ${user.name}`}
              </h2>
              {!readOnly && <div className={cx(styles.collectionsTools, isMobile && styles.collapsed)}>
                <PopoverFilter
                  hidePopoverLabel
                  disabled={disableFilter}
                  disabledTitle={fm(messages.followToFilter)}
                  label={filterLabel}
                  className={styles.ownershipPopover}>
                  <ul className={popoverMenuStyles.PopoverMenu}>
                    <RadioGroup
                      style={{ margin: '0 0' }}
                      horizontal
                      options={options}
                      selected={selected}
                      radioClass={styles.radioClass}
                      onChange={onOwnershipChange} />
                  </ul>
                </PopoverFilter>
                <div style={{ display: 'flex' }}>
                  <CTA
                    secondary
                    className={styles.shareCTA}
                    onClick={() => showModal(SHARE_MODAL, {
                      overlayTitle: <T id='Collections.shareYourCollections' defaultMessage='Share your collections' />,
                      objectType: 'collections',
                      objectUrl: `${shareRoot}/collections?privacy_code=${collectionsPrivacyCode.code}`
                    })}>
                    {fm(commonVerbs.share)}
                  </CTA>
                  <CTA
                    secondary
                    margins={false}
                    className={styles.newCTA}
                    onClick={() => showModal(CREATE_EDIT_COLLECTION_DIALOG, {
                      modalTitle: fm(collectionsMessages.newCollection),
                      allowVisibilityUpdate
                    })}>
                    {fm(collectionsMessages.newCollection)}
                  </CTA>
                </div>
              </div>}
              <div className={cx(styles.collectionCards, isMobile && styles.collapsed)}>
                {collections.toIndexedSeq().map(c => <CollectionCard key={c.id} collection={c} currentUser={currentUser} isMobile={isMobile} />)}
              </div>
            </>
            : <EmptyComponent renderEmpty={renderEmpty} />}
        </div>
      </>
  )
}

const NoCollections = ({ renderEmpty }) => {
  const fm = useFormatMessage()
  return renderEmpty
    ? <div style={{ height: '340px' }} />
    : <div className={styles.empty}>
      <h2 data-heading-alt>
        <T id='Collections.emptyHeader' defaultMessage='Welcome to Collections' />
      </h2>
      <Card noPadding className={styles.emptyCard} onClick={() => showModal(CREATE_EDIT_COLLECTION_DIALOG, {
        modalTitle: fm(collectionsMessages.newCollection)
      })}>
        <div className={styles.emptyCardTop}>
          <T id='Collections.emptyCardTop' defaultMessage='Create Collection' />
        </div>
        <div className={styles.emptyCardBottom}>
          <T
            id='Collections.emptyCardBottom'
            defaultMessage='Collections are the best way to organize your routes and rides, and to share them with others.' />
        </div>
      </Card>
    </div>
}

const CollectionsWithCollapseState = withCollapseState(PersonalCollections)

const loadInitialResources = ({ loadCollections, match, location, currentUser }) => {
  const userId = match.params.userId ? parseInt(match.params.userId, 10) : undefined
  const privacyCode = queryString.parse(location.search).privacy_code
  return promiseMapAll({
    _: loadCollections({
      extras: 'privacy_code,user',
      userId,
      privacyCode
    }, { identifier: userId ?? currentUser.id })
  })
}

const LoadableCollections = (props) => {
  const fm = useFormatMessage()
  return <Loadable
    {...props}
    component={CollectionsWithCollapseState}
    loadingText={fm(messages.loadableText)}
    resourceLoaded={() => loadInitialResources(props)} />
}

const mapStateToProps = (state, ownProps) => {
  const { currentUser } = state
  const user = ownProps.user || userSelectors.find(state, +ownProps.match.params.userId) || currentUser
  return {
    currentUser,
    user,
    collections: collectionSelectors.allForUser(state, user.id),
    collectionsPrivacyCode: privacyCodeSelectors.byParentWithScope(state, 'User', state.currentUser.id, 'collections')
  }
}

const mapDispatchToProps = {
  setPreference: userActions.setPreference,
  loadCollections: collectionActions.list
}

export const Collections = withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(LoadableCollections))

export const CollectionsPage = () => {
  return (
    <StandardLayout greyBg>
      <Collections readOnly />
    </StandardLayout>
  )
}
